import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import { getErrorMessage } from '../api/errors';
import { PageSeo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCoupon } from '../context/CouponContext';
import { useOrders } from '../context/OrderContext';
import { paymentService } from '../services/payments/paymentService';
import {
  clearPendingIyzicoCheckout,
  isIyzicoCheckoutCompleted,
  loadIyzicoCompletedOrder,
  loadPendingIyzicoCheckout,
  runIyzicoCompleteOnce,
  saveIyzicoCompletedOrder,
} from '../utils/iyzicoPending';
import { formatOrderNumber } from '../utils/formatOrderNumber';

export default function CheckoutResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { clearCart } = useCart();
  const { removeCoupon } = useCoupon();
  const { refreshOrders } = useOrders();

  const token = searchParams.get('token') ?? searchParams.get('paymentToken');
  const conversationId = searchParams.get('conversationId');
  const statusParam = searchParams.get('status');

  const [completedOrder, setCompletedOrder] = useState<{ id: string; orderNumber?: number } | null>(
    null,
  );
  const [failed, setFailed] = useState(statusParam === 'failure');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(token) && statusParam !== 'failure');

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setLoading(false);
      navigate('/login', { state: { from: '/checkout/result' }, replace: true });
      return;
    }

    if (statusParam === 'failure' && !token) {
      setFailed(true);
      setLoading(false);
      return;
    }

    if (!token) {
      setError('Ödeme doğrulama bilgisi bulunamadı.');
      setLoading(false);
      return;
    }

    if (isIyzicoCheckoutCompleted(token)) {
      const savedOrder = loadIyzicoCompletedOrder(token);
      if (savedOrder) setCompletedOrder(savedOrder);
      setLoading(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const pending = loadPendingIyzicoCheckout(token, conversationId);
        const resolvedConversationId = conversationId ?? pending?.conversationId;

        if (!resolvedConversationId || !pending?.order) {
          throw new Error(
            'Ödeme oturumu bulunamadı. Tarayıcı oturumu kapandıysa siparişlerinizi kontrol edin veya ödemeyi tekrar başlatın.',
          );
        }

        const result = await runIyzicoCompleteOnce(token, () =>
          paymentService.completeIyzico({
            token,
            conversationId: resolvedConversationId,
            order: pending.order,
          }),
        );

        if (cancelled) return;

        clearPendingIyzicoCheckout(token, resolvedConversationId);

        if (result.paymentStatus === 'success') {
          saveIyzicoCompletedOrder(token, {
            id: result.order.id,
            orderNumber: result.order.orderNumber,
          });
          setCompletedOrder({
            id: result.order.id,
            orderNumber: result.order.orderNumber,
          });
          // Backend complete sırasında sepeti zaten temizler; DELETE /cart tekrar çağırma.
          clearCart({ syncServer: false });
          removeCoupon();
          void refreshOrders();
        } else {
          setFailed(true);
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof Error && err.message === 'IYZICO_ALREADY_COMPLETED') {
            const savedOrder = loadIyzicoCompletedOrder(token);
            if (savedOrder) {
              setCompletedOrder(savedOrder);
              return;
            }
          }
          setError(getErrorMessage(err, 'Ödeme doğrulanamadı.'));
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, token, conversationId, statusParam, navigate, clearCart, removeCoupon, refreshOrders]);

  if (authLoading || (!user && loading)) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ödeme Doğrulanıyor" noindex />
        <p>Ödemeniz doğrulanıyor, lütfen bekleyin...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ödeme Doğrulanıyor" noindex />
        <p>Ödemeniz doğrulanıyor, lütfen bekleyin...</p>
      </div>
    );
  }

  if (completedOrder) {
    return (
      <div className="container empty-state">
        <PageSeo title="Sipariş Alındı" noindex />
        <div className="order-success">
          <span className="success-icon">
            <AppIcon icon={Check} size={32} strokeWidth={2.5} />
          </span>
          <h2>Siparişiniz Alındı!</h2>
          <p>
            iyzico ile ödemeniz başarıyla tamamlandı. Sipariş numaranız:{' '}
            <strong>{formatOrderNumber(completedOrder)}</strong>
          </p>
          <div className="hero-actions">
            <Link to="/orders" className="btn btn-primary">
              Siparişlerim
            </Link>
            <Link to="/shop" className="btn btn-outline">
              Alışverişe Devam Et
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container empty-state">
      <PageSeo title="Ödeme Başarısız" noindex />
      <div className="empty-cart">
        <span className="empty-icon">
          <AppIcon icon={X} size={64} strokeWidth={1.5} />
        </span>
        <h2>{failed ? 'Ödeme Tamamlanamadı' : 'Bir Hata Oluştu'}</h2>
        <p>{error || 'Ödeme işlemi iptal edildi veya başarısız oldu. Lütfen tekrar deneyin.'}</p>
        <div className="hero-actions">
          <Link to="/checkout" className="btn btn-primary">
            Ödemeye Dön
          </Link>
          <Link to="/orders" className="btn btn-outline">
            Siparişlerim
          </Link>
          <Link to="/cart" className="btn btn-outline">
            Sepete Git
          </Link>
        </div>
      </div>
    </div>
  );
}
